import { useState } from 'react'
import { faChevronDown, faMinus } from '@fortawesome/pro-solid-svg-icons'

import { Icon, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import {
  featureLabelForId,
  planTierServicesFor,
} from '@/constants/productFeatureConstants'
import { useLoadedFlagValue, useReselector } from '@/utils/sharedHooks'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import {
  selectIsCurrentUserScorp,
  selectIsGroupPractice,
} from '@/selectors/user.selectors'
import {
  displayRowKeysFor,
  displayRowLabel,
} from '@/features/user/settings/sections/plan-change/lib/taxFilingRows'
import { FeatureDetailLines } from '@/features/user/settings/sections/plan-change/FeatureDetailLines'
import {
  annualPriceCopy,
  badgeCopyForRationale,
  priceCopyForPrice,
  priceDisplayParts,
  tierCardCopyForTier,
} from './recommendedPlan.copy'
import {
  BILLING_INTERVALS,
  priceForInterval,
  type BillingInterval,
  type RankedTierOffer,
} from './recommendation.types'

export interface TierCardProps {
  offer: RankedTierOffer
  interval: BillingInterval
  onSelect: (offer: RankedTierOffer) => void
  /** Whether this is the tier the due-today total is currently quoting. */
  selected?: boolean
  disabled?: boolean
  /** Whether a recommendation is meaningful here. The list decides: a badge
   *  singling one card out of one card recommends nothing, and reads instead
   *  as a label on the product itself. */
  showRecommendation?: boolean
  /** Selects the trial wording of the dedicated expert detail lines: nothing on this
   *  card may imply dedicated support during a trial. */
  freeTrialEnabled?: boolean
}

export const TierCard = ({
  offer,
  interval,
  onSelect,
  selected,
  disabled,
  showRecommendation = true,
  freeTrialEnabled = false,
}: TierCardProps) => {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const badge =
    offer.isRecommended && showRecommendation
      ? badgeCopyForRationale(offer.rationale)
      : null
  const price = priceForInterval(offer, interval)
  const priceParts = priceDisplayParts(price)
  const annualPrice =
    interval === BILLING_INTERVALS.year
      ? annualPriceCopy(priceForInterval(offer, BILLING_INTERVALS.month), price)
      : null
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const isGroupPractice = useReselector(selectIsGroupPractice)
  const copyFromTier = useLoadedFlagValue(
    FEATURE_FLAG_KEYS.planCopyFromTier,
    false
  )
  const rows = copyFromTier
    ? [
        ...displayRowKeysFor(offer.featureIds),
        ...planTierServicesFor({
          tier: offer.tier,
          isLegacy: false,
          isScorp,
          isGroupPractice,
        }),
      ].map((key) => ({ key, label: displayRowLabel(key) }))
    : offer.featureIds.map((key) => ({ key, label: featureLabelForId(key) }))

  const copy = tierCardCopyForTier(offer.tier)
  const hasDetails = rows.length > 0 || Boolean(copy?.missing.length)
  const highlighted = selected

  const select = () => {
    if (!disabled) onSelect(offer)
  }

  return (
    <div className="min-w-[204px] flex-1">
      <div
        data-testid={`tier-${offer.tier}`}
        role="radio"
        aria-checked={Boolean(selected)}
        aria-label={offer.displayName}
        tabIndex={disabled ? -1 : 0}
        onClick={select}
        onKeyDown={(event) => {
          // Keydown bubbles from the controls inside the card, so without this
          // Enter on "Plan details" would open the details and select the tier.
          // `onClick`'s stopPropagation does not cover it: the key event
          // travels separately from the click it synthesizes.
          if (event.target !== event.currentTarget) return
          if (event.key !== 'Enter' && event.key !== ' ') return
          // Space scrolls the page otherwise, and Enter would submit anything
          // this card is ever nested inside.
          event.preventDefault()
          select()
        }}
        className={mtc(
          'flex h-full flex-col gap-4 box-border rounded-xl p-6 transition-shadow duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-moss-200 focus-visible:ring-offset-2',
          disabled ? 'cursor-default' : 'cursor-pointer',
          highlighted
            ? 'border-2 border-moss-200 bg-moss-0 shadow-[0_2px_4px_-1px_rgba(0,0,0,0.06),0_4px_6px_-1px_rgba(0,0,0,0.10)] hover:shadow-[0_4px_6px_-2px_rgba(0,0,0,0.05),0_10px_15px_-3px_rgba(0,0,0,0.10)]'
            : 'border border-neutral-200 bg-neutral-0 hover:shadow-[0_4px_6px_-2px_rgba(0,0,0,0.05),0_10px_15px_-3px_rgba(0,0,0,0.10)]'
        )}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Text variant={TypographyStyles.DISPLAY_XS}>
              {offer.displayName}
            </Text>
            {badge && (
              <span className="rounded-full bg-moss-200 px-[9px] py-[3px]">
                <Text
                  variant={TypographyStyles.EYEBROW}
                  className="text-neutral-0"
                >
                  {badge}
                </Text>
              </span>
            )}
          </div>

          {annualPrice ? (
            <div className="flex flex-col gap-0.5">
              <div className="flex items-baseline gap-1">
                <Text variant={TypographyStyles.DISPLAY_MD}>
                  {annualPrice.proratedMonthly}
                </Text>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  color={TextColors.MUTED}
                >
                  /mo
                </Text>
              </div>
              {annualPrice.monthlyRate && (
                <Text
                  variant={TypographyStyles.BODY_SM}
                  color={TextColors.MUTED}
                  className="line-through"
                >
                  {`${annualPrice.monthlyRate}/mo`}
                </Text>
              )}
            </div>
          ) : priceParts ? (
            <div className="flex items-baseline gap-1">
              <Text variant={TypographyStyles.DISPLAY_MD}>
                {priceParts.amount}
              </Text>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                {priceParts.unit}
              </Text>
            </div>
          ) : (
            <Text variant={TypographyStyles.DISPLAY_MD}>
              {priceCopyForPrice(price)}
            </Text>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {copy?.description && (
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              {copy.description}
            </Text>
          )}

          {hasDetails && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                setDetailsOpen((v) => !v)
              }}
              className="body-xs flex w-fit items-center gap-1 text-neutral-500 underline"
            >
              {detailsOpen ? 'Hide plan details' : 'Plan details'}
              <Icon
                icon={faChevronDown}
                size="2xs"
                color="text-neutral-500"
                className={mtc(
                  'transition-transform',
                  detailsOpen && 'rotate-180'
                )}
              />
            </button>
          )}

          {hasDetails && detailsOpen && (
            <div className="flex flex-col gap-2">
              {rows.length > 0 && (
                <ul className="mt-2 flex flex-col gap-1">
                  {rows.map(({ key, label }) => (
                    <li key={key} className="flex items-start gap-2">
                      <span aria-hidden className="text-moss-200">
                        ✓
                      </span>
                      <div className="flex flex-col">
                        <Text
                          variant={TypographyStyles.BODY_SM}
                          className="text-neutral-700"
                        >
                          {label}
                        </Text>
                        <FeatureDetailLines
                          featureId={key}
                          isTrial={freeTrialEnabled}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {copy && copy.missing.length > 0 && (
                <>
                  <hr className="my-3 border-neutral-200" />
                  <ul className="flex flex-col gap-1">
                    {copy.missing.map((label) => (
                      <li key={label} className="flex items-start gap-2">
                        {/* Boxed to one line of body-sm (14px at 150%) and
                            centred in it, so the minus sits on the middle of
                            the label's first line rather than near its
                            baseline, where it reads as an underscore. */}
                        <span className="flex h-[21px] shrink-0 items-center">
                          <Icon
                            icon={faMinus}
                            size="2xs"
                            color="text-neutral-400"
                          />
                        </span>
                        <Text
                          variant={TypographyStyles.BODY_SM}
                          color={TextColors.MUTED}
                        >
                          {label}
                        </Text>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
